import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { http } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Waves, Sunrise, Pencil, Save, X, MapPin, ArrowRight } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from "recharts";

function num(n) { return n == null ? "–" : Math.round(n * 10) / 10; }
function dirToCompass(deg) {
  if (deg == null) return "–";
  const dirs = ["N","NNE","NE","ENE","E","ESE","SE","SSE","S","SSW","SW","WSW","W","WNW","NW","NNW"];
  return dirs[Math.round(((deg % 360) / 22.5)) % 16];
}
function fmtHour(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length === 16 ? iso + ":00" : iso);
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function BeachNotes({ beachId, note, onSaved }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(note?.text || "");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => { queueMicrotask(() => setValue(note?.text || "")); }, [note, beachId]);

  const isAdmin = user?.role === "admin";
  const hasNote = note?.text && note.text.length > 0;

  const save = async () => {
    setBusy(true);
    try {
      await http.put(`/beach-notes/${beachId}`, { text: value });
      queueMicrotask(() => { setEditing(false); onSaved?.(); });
    } finally {
      queueMicrotask(() => setBusy(false));
    }
  };

  if (!hasNote && !isAdmin) return null;

  return (
    <div data-testid="beach-notes" className="mt-4 rounded-2xl bg-[color:var(--coral)]/8 border border-[color:var(--coral)]/30 p-4">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex items-center gap-1.5">
          <Pencil className="w-3.5 h-3.5 text-[color:var(--coral)]" />
          <p className="eyebrow text-[color:var(--coral)]">{t("beach_notes")}</p>
        </div>
        {isAdmin && !editing && (
          <Button
            data-testid="edit-beach-note"
            size="sm"
            variant="ghost"
            onClick={() => setEditing(true)}
            className="text-xs h-6 px-2"
          >
            {hasNote ? t("edit") : "+ " + t("edit")}
          </Button>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <Textarea
            data-testid="beach-note-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={3}
            maxLength={400}
            placeholder={t("beach_note_placeholder")}
            className="rounded-xl bg-white"
          />
          <div className="flex items-center gap-2 justify-end">
            <Button
              data-testid="cancel-beach-note"
              size="sm"
              variant="ghost"
              onClick={() => { setEditing(false); setValue(note?.text || ""); }}
            >
              <X className="w-3.5 h-3.5 mr-1" />{t("cancel")}
            </Button>
            <Button
              data-testid="save-beach-note"
              size="sm"
              onClick={save}
              disabled={busy || !value.trim()}
              className="pill bg-[color:var(--navy)] text-white hover:bg-[color:var(--navy-2)]"
            >
              <Save className="w-3.5 h-3.5 mr-1" />
              {busy ? "…" : t("save")}
            </Button>
          </div>
        </div>
      ) : hasNote ? (
        <>
          <p data-testid="beach-note-text" className="text-sm text-[color:var(--ink)] leading-relaxed">{note.text}</p>
          {note.updated_at && (
            <p className="text-[10px] text-[color:var(--ink-2)] mt-1.5 uppercase tracking-widest">
              {new Date(note.updated_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
            </p>
          )}
        </>
      ) : (
        <p className="text-sm text-[color:var(--ink-2)] italic">{t("no_notes_yet")}</p>
      )}
    </div>
  );
}

export default function SurfReport({ data, onNoteSaved }) {
  const { t } = useI18n();
  const daily = data?.marine?.daily;

  const days = (daily?.time || []).map((d, i) => ({
    date: d,
    label: new Date(d + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }),
    wave: daily?.wave_height_max?.[i],
    period: daily?.wave_period_max?.[i],
    dir: daily?.wave_direction_dominant?.[i],
  }));

  if (!days.length) {
    return (
      <div data-testid="surf-report" className="clay p-6 md:p-7">
        <p className="eyebrow">{t("surf_report")}</p>
        <p className="text-sm text-[color:var(--ink-2)] italic mt-3">
          {t("surf_unavailable")}
        </p>
      </div>
    );
  }
  const today = days[0];
  const best = data?.best_window;

  return (
    <div data-testid="surf-report" className="clay p-6 md:p-7">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="eyebrow">{t("surf_report")}</p>
          <h3 className="text-2xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>
            {today.wave != null ? `${num(today.wave)} m` : "—"}
            <span className="text-sm text-[color:var(--ink-2)] ml-2 font-normal">{t("wave_height")} · {t("today")}</span>
          </h3>
        </div>
        <div className="w-12 h-12 rounded-2xl bg-[color:var(--navy)]/10 text-[color:var(--navy)] flex items-center justify-center">
          <Waves className="w-6 h-6" />
        </div>
      </div>

      <Tabs defaultValue="today">
        <TabsList className="grid grid-cols-2 w-fit rounded-full bg-[color:var(--sand-2)] p-1">
          <TabsTrigger value="today" data-testid="surf-tab-today" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            {t("today")}
          </TabsTrigger>
          <TabsTrigger value="week" data-testid="surf-tab-week" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            {t("week")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="today" className="mt-4 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Stat label={t("wave_height")} value={`${num(today.wave)} m`} testid="surf-wave-today" />
            <Stat label={t("wave_period")} value={`${num(today.period)} s`} testid="surf-period-today" />
            <Stat label="Dir" value={dirToCompass(today.dir)} testid="surf-dir-today" />
          </div>
          {best && (
            <div data-testid="best-window" className="rounded-2xl bg-[color:var(--teal)]/10 p-4 flex items-center gap-3">
              <Sunrise className="w-5 h-5 text-[color:var(--teal)] shrink-0" />
              <div className="flex-1">
                <p className="eyebrow text-[color:var(--teal)]">{t("best_window")}</p>
                <p className="font-semibold mt-0.5" style={{ fontFamily: "Outfit" }}>
                  {fmtHour(best.start)} – {fmtHour(best.end)}
                </p>
                <p className="text-xs text-[color:var(--ink-2)] mt-0.5">
                  {t("peak")} {fmtHour(best.peak_time)} · {best.peak_wave_m} m · {best.peak_wind_kmh} km/h {t("wind_label")}
                </p>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="week" className="mt-4 space-y-4">
          <SwellChart days={days} />
          <div data-testid="surf-week-list" className="grid gap-2">
            {days.map((d) => (
              <div key={d.date} data-testid={`surf-day-${d.date}`} className="flex items-center justify-between rounded-2xl px-4 py-3 bg-[color:var(--sand-2)]">
                <span className="font-medium text-sm">{d.label}</span>
                <div className="flex items-center gap-4 text-sm">
                  <span className="mono"><strong>{num(d.wave)}</strong> m</span>
                  <span className="text-[color:var(--ink-2)] mono">{num(d.period)} s</span>
                  <span className="text-[color:var(--ink-2)]">{dirToCompass(d.dir)}</span>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <BeachNotes beachId={data?.beach?.id} note={data?.beach_note} onSaved={onNoteSaved} />

      {/* Deep-link into the full per-beach surf guide + all-beaches index */}
      <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
        {data?.beach?.id && (
          <Link
            to={`/surf/${data.beach.id}`}
            data-testid="surf-report-full-guide-link"
            className="pill inline-flex items-center gap-1 px-3 py-1.5 bg-[color:var(--navy)] text-white hover:bg-[color:var(--navy-2)]"
          >
            <MapPin className="w-3.5 h-3.5" /> Full {data.beach.name} guide
          </Link>
        )}
        <Link
          to="/beaches"
          data-testid="surf-report-all-beaches-link"
          className="inline-flex items-center gap-1 text-[color:var(--coral)] hover:underline font-semibold"
        >
          See all beach guides <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl bg-[color:var(--sand-2)] p-4">
      <p className="text-[11px] uppercase tracking-wider text-[color:var(--ink-2)]">{label}</p>
      <p className="text-xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>{value}</p>
    </div>
  );
}

function SwellChart({ days }) {
  const { t } = useI18n();
  if (!days || days.length === 0) return null;

  // Build chart data: one point per day with wave height (m) + period (s).
  // Arrow chips render direction below the chart so we keep the axis clean.
  const data = days.map((d) => ({
    label: new Date(d.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric" }),
    wave: d.wave == null ? null : Math.round(d.wave * 10) / 10,
    period: d.period == null ? null : Math.round(d.period * 10) / 10,
    dir: d.dir,
    date: d.date,
  }));
  const peakWave = Math.max(0, ...data.map((d) => (d.wave == null ? 0 : d.wave)));
  // Round up to the nearest 0.5m so the y-axis labels stay clean (e.g. 1.5m, 2m, 2.5m).
  const maxWave = Math.max(2, Math.ceil(peakWave * 1.25 * 2) / 2);

  return (
    <div data-testid="swell-chart" className="rounded-2xl border border-[color:var(--hairline)] bg-white/60 p-4">
      <div className="flex items-baseline justify-between mb-2">
        <p className="eyebrow">{t("swell_forecast_title")}</p>
        <p className="text-[10px] uppercase tracking-widest text-[color:var(--ink-2)]">
          {t("swell_forecast_legend")}
        </p>
      </div>
      <div className="h-44" style={{ minHeight: 176 }}>
        <ResponsiveContainer width="100%" height="100%" minHeight={176}>
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="swellFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1B3B6F" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#1B3B6F" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#E7E2D8" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "#5C757D" }}
              tickLine={false}
              axisLine={{ stroke: "#E7E2D8" }}
            />
            <YAxis
              yAxisId="m"
              domain={[0, maxWave]}
              tick={{ fontSize: 10, fill: "#1B3B6F" }}
              tickLine={false}
              axisLine={false}
              width={44}
              tickFormatter={(v) => `${v}m`}
            />
            <YAxis
              yAxisId="s"
              orientation="right"
              domain={[0, "dataMax + 4"]}
              tick={{ fontSize: 10, fill: "#E76F51" }}
              tickLine={false}
              axisLine={false}
              width={36}
              tickFormatter={(v) => `${v}s`}
            />
            <Tooltip
              cursor={{ stroke: "#1B3B6F", strokeOpacity: 0.15 }}
              contentStyle={{ borderRadius: 12, border: "1px solid #E7E2D8", fontSize: 12 }}
              formatter={(val, key) => {
                if (key === "wave") return [`${val} m`, t("wave_height")];
                if (key === "period") return [`${val} s`, t("wave_period")];
                return [val, key];
              }}
              labelFormatter={(l) => l}
            />
            <ReferenceLine yAxisId="m" y={1} stroke="#5C757D" strokeDasharray="2 4" strokeOpacity={0.4} />
            <Area
              yAxisId="m"
              type="monotone"
              dataKey="wave"
              stroke="#1B3B6F"
              strokeWidth={2.5}
              fill="url(#swellFill)"
              dot={{ r: 3, fill: "#1B3B6F" }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
            <Line
              yAxisId="s"
              type="monotone"
              dataKey="period"
              stroke="#E76F51"
              strokeWidth={2}
              strokeDasharray="4 3"
              dot={{ r: 2.5, fill: "#E76F51" }}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {/* Direction arrows under each day */}
      <div className="mt-2 grid gap-0.5" style={{ gridTemplateColumns: `repeat(${data.length}, minmax(0,1fr))` }}>
        {data.map((d) => (
          <div
            key={d.date}
            data-testid={`swell-dir-${d.date}`}
            className="flex flex-col items-center text-[10px] text-[color:var(--ink-2)] gap-0.5"
          >
            <span
              aria-hidden
              style={{
                display: "inline-block",
                transform: `rotate(${(d.dir ?? 0) + 180}deg)`,
                color: "#1B3B6F",
                fontSize: 14,
                lineHeight: 1,
              }}
            >
              ↑
            </span>
            <span className="mono">{dirToCompass(d.dir)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
